/**
 * Client-side DTMF key-tone playback.
 *
 * Amazon Connect Streams' `connection.sendDigits()` is pure signaling — the SDK
 * plays no audio itself (the real CCP generates keypad sounds in its own UI).
 * A custom UI must generate the local key-press tone, which is what this does:
 * a short dual-frequency sine burst played in the browser only. The tone never
 * reaches the line — the softphone transmits the real DTMF via signaling.
 *
 * Tone synthesis lives in audioTone (shared with the chat message chime).
 */

import { getAudioContext, playToneBurst } from '@/utils/audioTone';

/** Low/high sine frequencies (Hz) per digit, ITU-T standard. */
const DTMF_FREQUENCIES: Record<string, [number, number]> = {
  '1': [697, 1209],
  '2': [697, 1336],
  '3': [697, 1477],
  '4': [770, 1209],
  '5': [770, 1336],
  '6': [770, 1477],
  '7': [852, 1209],
  '8': [852, 1336],
  '9': [852, 1477],
  '*': [941, 1209],
  '0': [941, 1336],
  '#': [941, 1477],
};

const TONE_DURATION_MS = 100;
const TONE_GAIN = 0.12;

/** Plays the local key-press tone for a DTMF digit. No-op for unknown keys. */
export function playDtmfTone(digit: string): void {
  const frequencies = DTMF_FREQUENCIES[digit];
  if (!frequencies) return;
  try {
    const ctx = getAudioContext();
    if (!ctx || ctx.state === 'closed') return;
    const startAt = ctx.currentTime;
    for (const frequency of frequencies) {
      playToneBurst(ctx, {
        frequency,
        startTime: startAt,
        durationSeconds: TONE_DURATION_MS / 1000,
        gain: TONE_GAIN,
      });
    }
  } catch {
    // Audio unavailable (headless browser, autoplay policy) — the digit still sends.
  }
}
